
import { defineComponent, ref, watch, computed } from "vue-demi";
import { debounce } from "throttle-debounce-ts";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import type { API_TSocItem } from "@/models/soc_items";

const ITEMS_LIMIT = 100;
const MIN_QUERY_LENGTH = 3;

export default defineComponent({
  name: "JobRoleSearch",

  props: {
    modelValue: {
      type: String,
      required: true,
    },
  },

  emits: ["update:modelValue", "set-job-role"],

  setup(props, { emit }) {
    const query = ref(props.modelValue);
    const items = ref<API_TSocItem[]>([]);
    const loading = ref(false);
    const selectedValue = ref(props.modelValue);

    const debouncedFetch = debounce(500, async () => {
      try {
        const { data } = await axios.get(
          API_ROUTES.SEARCH_SOC_CODE(query.value, items.value.length, ITEMS_LIMIT)
        );

        if (!data) {
          items.value = [];
          return;
        }

        items.value = data.map((v) => ({
          label: v.job_title,
          value: v.job_title,
          code: v.soc_code,
          description: v.description,
        }));
      } finally {
        loading.value = false;
      }
    });

    const fetchItems = () => {
      loading.value = true;

      debouncedFetch();
    };

    const filteredItems = computed(() => {
      return items.value;
    });

    watch(() => props.modelValue, (val) => {
      query.value = val;

      selectedValue.value = val;
    });

    watch(selectedValue, (val) => {
      emit("update:modelValue", val);
    });

    watch(query, (val) => {
      const chosenItem = filteredItems.value.find(v => v.label === val)

      if (chosenItem) {
        emit("set-job-role", chosenItem);

        items.value = [chosenItem];

        return;
      }

      if (val.length >= MIN_QUERY_LENGTH && !loading.value) {

        items.value = [];

        fetchItems();
      }
    });

    const counterLabel = computed(() => {
      return filteredItems.value.length
        ? `${filteredItems.value.length} job role(s) found`
        : "No results";
    });

    function onSearch(value: string) {
      query.value = value;
    }

    return {
      selectedValue,
      query,
      loading,
      filteredItems,
      counterLabel,
      onSearch,
    };
  },
});
